@switch (previewStatus()) {
    @case ('error') {
        <div
            class="preview-viewer-shell bg-muted"
            [style.height]="previewHeight"
        >
            @if (leftPanels().length) {
                <div class="preview-left-control">
                    <qpx-preview-panel-buttons
                        [panels]="leftPanels()"
                        [viewerReady]="false"
                        [activePanelId]="panelState.activeLeftPanelId()"
                        (panelToggle)="panelState.togglePanel($event)"
                    />
                </div>
            }
            <div class="preview-close-control">
                <qpx-preview-panel-buttons
                    [panels]="rightPanels()"
                    [viewerReady]="false"
                    [activePanelId]="panelState.activeRightPanelId()"
                    (panelToggle)="panelState.togglePanel($event)"
                />
                <qpx-preview-action-buttons [actions]="errorActions()" />

                @if (openPreviewSourceUrl(); as sourceUrl) {
                    <a
                        qpxButton
                        matIconButton
                        context="secondary"
                        tone="muted"
                        [href]="sourceUrl"
                        target="_blank"
                        rel="noopener noreferrer"
                        [attr.aria-label]="`Open ${fileName()}`"
                        qpxTooltip="Open document"
                        matTooltipPosition="below"
                    >
                        <ng-icon qpx name="lucideExternalLink" />
                    </a>
                }

                <button
                    qpxButton
                    matIconButton
                    context="secondary"
                    tone="muted"
                    aria-label="Close preview"
                    (click)="onClosePreview()"
                >
                    <ng-icon qpx name="lucideX" />
                </button>
            </div>

            <qpx-error-state
                role="alert"
                [heading]="errorHeading()"
                [error]="errorDetails()"
                [style.height]="previewHeight"
                visualSize="hero"
            >
                <ng-container action>
                    @if (canRetry()) {
                        <button
                            qpxButton
                            matButton="elevated"
                            size="sm"
                            context="card"
                            [disabled]="refreshing()"
                            (click)="retryPreview()"
                        >
                            @if (refreshing()) {
                                <qpx-loader
                                    matButtonIcon
                                    size="sm"
                                    [context]="undefined"
                                    containerClass="mat-icon inline-flex shrink-0"
                                    aria-hidden="true"
                                />
                            } @else {
                                <ng-icon
                                    qpx
                                    name="lucideRefreshCw"
                                    matButtonIcon
                                />
                            }
                            <span>{{
                                refreshing() ? 'Retrying…' : 'Retry'
                            }}</span>
                        </button>
                    }
                </ng-container>
            </qpx-error-state>
        </div>
    }
    @default {
        <div
            class="preview-viewer-shell bg-muted"
            [style.height]="previewHeight"
        >
            @if (previewStatus() === 'loading') {
                <qpx-loading-state
                    class="preview-loading-state"
                    ariaLabel="Loading document preview"
                />
            }
            @for (source of rendererSources(); track source) {
                @if (source.previewUrl; as url) {
                    @switch (fileType()) {
                        @case (FILE_PREVIEW_TYPE.Pdf) {
                            @defer (on immediate) {
                                <qpx-pdf-preview
                                    [previewUrl]="url"
                                    [fileName]="source.fileName"
                                    [initialPage]="initialPage()"
                                    [searchTerm]="searchTerm()"
                                    [actions]="actions()"
                                    [panels]="panels()"
                                    [panelsMountedByHost]="true"
                                    [infoPanel]="infoPanel()"
                                    [previewHeight]="previewHeight"
                                    (viewerLoaded)="onViewerLoaded(source)"
                                    (viewerLoadError)="
                                        onViewerLoadError($event, source)
                                    "
                                    (closePreview)="onClosePreview()"
                                />
                            } @error {
                                <qpx-preview-renderer-load-error
                                    (loadError)="
                                        onViewerLoadError($event, source)
                                    "
                                />
                            }
                        }
                        @case (FILE_PREVIEW_TYPE.Image) {
                            @defer (on immediate) {
                                <qpx-image-preview
                                    [previewUrl]="url"
                                    [fileName]="source.fileName"
                                    [actions]="actions()"
                                    [panels]="panels()"
                                    [panelsMountedByHost]="true"
                                    [infoPanel]="infoPanel()"
                                    [previewHeight]="previewHeight"
                                    (viewerLoaded)="onViewerLoaded(source)"
                                    (viewerLoadError)="
                                        onViewerLoadError($event, source)
                                    "
                                    (closePreview)="onClosePreview()"
                                />
                            } @error {
                                <qpx-preview-renderer-load-error
                                    (loadError)="
                                        onViewerLoadError($event, source)
                                    "
                                />
                            }
                        }
                        @default {
                            @defer (on immediate) {
                                <qpx-document-preview
                                    [previewUrl]="url"
                                    [fileName]="source.fileName"
                                    [actions]="actions()"
                                    [panels]="panels()"
                                    [panelsMountedByHost]="true"
                                    [infoPanel]="infoPanel()"
                                    [previewHeight]="previewHeight"
                                    (viewerLoaded)="onViewerLoaded(source)"
                                    (viewerLoadError)="
                                        onViewerLoadError($event, source)
                                    "
                                    (closePreview)="onClosePreview()"
                                />
                            } @error {
                                <qpx-preview-renderer-load-error
                                    (loadError)="
                                        onViewerLoadError($event, source)
                                    "
                                />
                            }
                        }
                    }
                }
            }
        </div>
    }
}

@for (panel of mountedPanels(); track `${panel.side}:${panel.id}`) {
    <qpx-preview-panel
        [side]="panel.side"
        [panelId]="panelId(panel)"
        [fileName]="fileName()"
        [panel]="panel"
        [visible]="
            panelState.controlsVisible() &&
            isPanelActive(panel) &&
            (panel.requiresViewerReady === false || previewStatus() === 'ready')
        "
    />
}
@if (previewStatus() === 'error') {
    @for (panel of errorPanels(); track `${panel.side}:${panel.id}`) {
        <qpx-preview-panel
            [side]="panel.side"
            [panelId]="panelId(panel)"
            [fileName]="fileName()"
            [panel]="panel"
        />
    }
}
